/**
 * Thornhaven CC0 Asset Registry
 * ------------------------------
 * Reusable registry so Thornhaven (and future Midgard) code references stable
 * ASSET IDs instead of hardcoded file paths. Data is auto-generated from the
 * imported GLB/GLTF files by scripts/generate_asset_registry.py.
 *
 * Asset id format:  <category>.<pack>.<model_slug>
 *   e.g. 'buildings.medieval-village.wall_unevenbrick_door_flat'
 *        'walls-gates.castle-kit.gate'
 *        'nature.stylized-nature.commontree_1'
 *        'props.fantasy-props.anvil'
 *
 * Usage:
 *   import { getAsset, assetUrl, getByCategory } from 'assets/thornhavenAssetRegistry';
 *   const url = assetUrl('props.fantasy-props.anvil'); // -> '/assets/thornhaven/props/fantasy-props/glTF/Anvil.gltf'
 *   useGLTF(assetUrl('props.fantasy-props.anvil'));
 *
 * All assets are CC0-1.0. See /assets/thornhaven/SOURCES.md for pack provenance.
 */

import data from './thornhavenAssets.json';

export const THORNHAVEN_ASSETS = data.assets;
export const ASSET_COUNT = data.count;
export const ASSET_CATEGORIES = ['buildings', 'walls-gates', 'nature', 'props'];
export const ASSET_PACKS = ['medieval-village', 'castle-kit', 'stylized-nature', 'fantasy-props'];

// Architectural separation: every imported CC0 pack is a "PLUGIN" asset.
// Native Midgard-authored prefabs carry sourceType 'native' (see prefabs/).
for (const a of data.assets) {
  a.sourceType = 'plugin';
  a.pluginName = a.pack_name || a.pack;
}

const _byId = Object.create(null);
for (const a of data.assets) _byId[a.id] = a;

/** Full asset record for an id, or null. */
export function getAsset(id) {
  return _byId[id] || null;
}

/** Public URL for an asset id (honors CRA PUBLIC_URL), or null if unknown. */
export function assetUrl(id) {
  const a = _byId[id];
  if (!a) return null;
  const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  return base + a.url;
}

/** All assets in a category: 'buildings' | 'walls-gates' | 'nature' | 'props'. */
export function getByCategory(category) {
  return data.assets.filter((a) => a.category === category);
}

/** All assets from a pack key, e.g. 'castle-kit'. */
export function getByPack(pack) {
  return data.assets.filter((a) => a.pack === pack);
}

/** Every known asset id. */
export const ASSET_IDS = data.assets.map((a) => a.id);

const registry = {
  assets: THORNHAVEN_ASSETS,
  count: ASSET_COUNT,
  categories: ASSET_CATEGORIES,
  packs: ASSET_PACKS,
  getAsset,
  assetUrl,
  getByCategory,
  getByPack,
  ASSET_IDS,
};

export default registry;
